import { type ChangeEvent, useState } from 'react'
import { parseCsvText } from './csv'
import { publishIndicatorPreview } from './data'
import { buildPeriodCandidates, prepareMatrix, type PeriodCandidate, type PreparedRows } from './prepare'
import { parseXlsxBuffer } from './xlsx'

interface StagedImport {
  fileName: string
  format: 'CSV' | 'XLSX'
  sheetName: string
  sha256: string
  prepared: PreparedRows
  periods: PeriodCandidate[]
}

interface ImportPanelProps {
  onPublished: () => void
}

type PublishState =
  | { status: 'idle' }
  | { status: 'publishing' }
  | { status: 'success'; batchId: string }
  | { status: 'error'; message: string }

function monthLabel(referenceMonth: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${referenceMonth}T12:00:00Z`))
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return [...new Uint8Array(digest)]
    .map(value => value.toString(16).padStart(2, '0'))
    .join('')
}

function totalVolume(period: PeriodCandidate) {
  return period.metrics.reduce((sum, metric) => sum + metric.attendance_count, 0)
}

function navigationVolume(period: PeriodCandidate) {
  return period.metrics
    .filter(metric => metric.queue_code === 'DEFAULT')
    .reduce((sum, metric) => sum + metric.attendance_count, 0)
}

export function ImportPanel({ onPublished }: ImportPanelProps) {
  const [staged, setStaged] = useState<StagedImport | null>(null)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [publishStates, setPublishStates] = useState<Record<string, PublishState>>({})

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || processing) return

    setProcessing(true)
    setError(null)
    setStaged(null)
    setPublishStates({})

    try {
      const extension = file.name.split('.').pop()?.toLowerCase()
      if (extension === 'xls') {
        throw new Error('O formato .xls legado não é suportado. Salve o arquivo como .xlsx ou .csv.')
      }
      if (extension !== 'xlsx' && extension !== 'csv') {
        throw new Error('Formato não suportado. Utilize .xlsx ou .csv.')
      }

      const buffer = await file.arrayBuffer()
      const sha256 = await sha256Hex(buffer)
      let rows: unknown[][]
      let sheetName: string
      let format: 'CSV' | 'XLSX'

      if (extension === 'csv') {
        const parsed = parseCsvText(new TextDecoder('utf-8').decode(buffer))
        rows = parsed.rows
        sheetName = 'CSV'
        format = 'CSV'
      } else {
        const parsed = await parseXlsxBuffer(buffer)
        rows = parsed.rows
        sheetName = parsed.sheetName
        format = 'XLSX'
      }

      const prepared = prepareMatrix(rows)
      const periods = buildPeriodCandidates(prepared.rows)
      if (!periods.length) {
        throw new Error('Nenhuma competência válida foi identificada no arquivo.')
      }

      setStaged({ fileName: file.name, format, sheetName, sha256, prepared, periods })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível processar o arquivo.')
    } finally {
      setProcessing(false)
    }
  }

  async function publish(period: PeriodCandidate) {
    if (!staged) return
    if (staged.prepared.missingRequired.length) {
      setError('A publicação está bloqueada porque existem colunas obrigatórias ausentes.')
      return
    }
    if (!period.metrics.length) {
      setError('Esta competência não possui registros válidos para publicação.')
      return
    }

    setError(null)
    setPublishStates(current => ({
      ...current,
      [period.referenceMonth]: { status: 'publishing' },
    }))

    try {
      const batchId = await publishIndicatorPreview({
        referenceMonth: period.referenceMonth,
        sourceFileName: staged.fileName,
        sourceSha256: staged.sha256,
        rowCount: period.sourceRowCount,
        metrics: period.metrics,
      })
      setPublishStates(current => ({
        ...current,
        [period.referenceMonth]: { status: 'success', batchId },
      }))
      onPublished()
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Não foi possível publicar a prévia.'
      setPublishStates(current => ({
        ...current,
        [period.referenceMonth]: { status: 'error', message },
      }))
    }
  }

  const blocked = Boolean(staged?.prepared.missingRequired.length)

  return (
    <section className="import-panel" aria-labelledby="import-title">
      <div className="import-heading">
        <div>
          <p className="eyebrow">Importação administrativa</p>
          <h2 id="import-title">Importar e conferir</h2>
          <p>
            O arquivo bruto é processado no navegador. Somente os agregados confirmados são
            publicados no histórico.
          </p>
        </div>
        <label className="file-button">
          <span>{processing ? 'Processando…' : 'Selecionar XLSX ou CSV'}</span>
          <input
            type="file"
            accept=".xlsx,.csv,.xls"
            disabled={processing}
            onChange={event => void handleFile(event)}
          />
        </label>
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}
      {processing && <p role="status" className="import-status">Lendo e validando o arquivo localmente…</p>}

      {!staged && !processing && !error && (
        <div className="import-empty">
          <strong>Nenhum arquivo em conferência.</strong>
          <span>Selecione uma exportação para validar antes de publicar qualquer dado.</span>
        </div>
      )}

      {staged && (
        <>
          <dl className="import-summary">
            <div><dt>Arquivo</dt><dd>{staged.fileName}</dd><small>{staged.format} · {staged.sheetName}</small></div>
            <div><dt>Linhas preparadas</dt><dd>{staged.prepared.rows.length}</dd><small>{staged.prepared.ignoredRows} ignoradas na leitura</small></div>
            <div><dt>Competências</dt><dd>{staged.periods.length}</dd><small>publicação individual por mês</small></div>
            <div><dt>Estrutura</dt><dd>{blocked ? 'Revisar' : 'Pronta'}</dd><small>{staged.prepared.recognizedColumns.length} campos reconhecidos</small></div>
          </dl>

          <div className="import-diagnostics">
            <div>
              <strong>Validação estrutural</strong>
              <span>
                {staged.prepared.missingRequired.length
                  ? `Obrigatórias ausentes: ${staged.prepared.missingRequired.join(', ')}.`
                  : 'Colunas obrigatórias localizadas.'}
              </span>
            </div>
            <div>
              <strong>Limpeza</strong>
              <span>
                {staged.prepared.descriptionRows} descritivas · {staged.prepared.repeatedHeaderRows} cabeçalhos repetidos · {staged.prepared.unrecognizedRows} não reconhecidas
              </span>
            </div>
          </div>

          <div className="import-periods">
            <div className="import-periods__head">
              <h3>Competências identificadas</h3>
              <p>Confira cada mês. Publicar substitui apenas uma prévia existente; definitivo ou fechado é protegido pelo banco.</p>
            </div>
            {staged.periods.map(period => {
              const state = publishStates[period.referenceMonth] ?? { status: 'idle' as const }
              const volume = totalVolume(period)
              const navigation = navigationVolume(period)
              return (
                <article className="import-period" key={period.referenceMonth}>
                  <div>
                    <strong>{monthLabel(period.referenceMonth)}</strong>
                    <span>{period.sourceRowCount} registros na competência · {period.publishableRowCount} dentro da regra de horário</span>
                  </div>
                  <div className="import-period__numbers">
                    <span><b>{volume}</b> volume</span>
                    <span><b>{navigation}</b> navegação</span>
                    <span><b>{period.metrics.length}</b> operador/fila</span>
                  </div>
                  <div className="import-period__action">
                    <button
                      type="button"
                      className="primary-button"
                      disabled={blocked || !period.metrics.length || state.status === 'publishing' || state.status === 'success'}
                      onClick={() => void publish(period)}
                    >
                      {state.status === 'publishing'
                        ? 'Publicando…'
                        : state.status === 'success'
                          ? 'Prévia publicada'
                          : 'Publicar prévia'}
                    </button>
                    {state.status === 'error' && <small role="alert">{state.message}</small>}
                    {state.status === 'success' && <small>Lote {state.batchId.slice(0, 8)}… registrado.</small>}
                  </div>
                </article>
              )
            })}
          </div>
        </>
      )}
    </section>
  )
}
